import type { AdminSelf } from '@saay/api-client';
import {
  formatLocalDate,
  formatTimestamp,
  formatToman,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';

import { formatLocalMobileDisplay } from '../display.js';
import {
  Banner,
  CapacityMeter,
  CreditCard,
  FactList,
  IconButton,
  Ltr,
  Masked,
  Money,
  Skeleton,
  StatusChip,
  Table,
  Tabs,
} from '../kit.js';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useOutletContext, useParams } from 'react-router';

import type { AdminBuyer, AdminCreditAllocation } from '../api.js';
import { AuditTrailLink } from '../components/audit-trail-link.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { LoginLockAction } from '../components/login-lock.js';
import { OPENED_FROM_LIST } from '../components/route-sheet.js';
import { canReadWallet } from '../permissions.js';
import { useBuyer } from '../queries.js';
import {
  BuyerLifecycleActions,
  BuyerPendingApprovals,
  CreditActionDialog,
  type CreditActionTarget,
  creditActionFor,
  RestrictionsSection,
} from './buyer-actions.js';
import { BuyerWalletPanel } from './buyer-wallet.js';
import { PhysicalCardHeader, PhysicalCardSection } from './physical-card.js';
import { DetailHeader } from '../components/detail-header.js';
import {
  BUYER_QUICK_ACTION_KEY,
  type BuyerListQuickAction,
  type BuyersOutletContext,
} from './buyers-list.js';

type BuyerTab =
  'summary' | 'credit' | 'card' | 'restrictions' | 'organizations' | 'history' | 'wallet';

const LINKABLE_TABS: readonly BuyerTab[] = [
  'summary',
  'credit',
  'card',
  'restrictions',
  'organizations',
  'history',
];

/** `?tab=card` opens a tab directly (a deep link, for example from «کارت‌های فیزیکی»). */
function tabFromSearch(search: string, self: AdminSelf): BuyerTab {
  const requested = new URLSearchParams(search).get('tab');
  const linkable = canReadWallet(self) ? [...LINKABLE_TABS, 'wallet' as const] : LINKABLE_TABS;
  return linkable.find((tab) => tab === requested) ?? 'summary';
}

function documentLabel(code: string): string {
  const label = statusLabel('identityDocumentType', code);
  return label === 'نامشخص' ? 'مدرک دیگر' : label;
}

function buyerHeadlineLabel(buyer: AdminBuyer): string {
  if (buyer.status === 'PENDING_VERIFICATION') {
    return 'در انتظار تأیید هویت';
  }
  if (buyer.status === 'SUSPENDED') {
    return 'تعلیق‌شده · بررسی ریسک';
  }
  return statusLabel('buyerStatus', buyer.status);
}

function creditUsageRate(totals: AdminBuyer['card']['totals']): string | null {
  const granted = totals.currentGrantedCreditIrr;
  if (granted <= 0) {
    return null;
  }
  const used = totals.outstandingPrincipalIrr + totals.reservedPrincipalIrr;
  return String(Math.min(1, used / granted));
}

function creditUsageText(totals: AdminBuyer['card']['totals']): string {
  return `بدهی ${formatToman(totals.outstandingPrincipalIrr)} · رزرو ${formatToman(totals.reservedPrincipalIrr)}`;
}

/** Identity and guarantee facts as labelled rows (W4: chips with counts read poorly). */
function verificationText(buyer: AdminBuyer): string {
  if (buyer.identityVerification === null) {
    return 'انجام نشده';
  }
  const doc =
    buyer.identityVerification.documentType === null
      ? ''
      : ` · ${documentLabel(buyer.identityVerification.documentType)}`;
  return `${statusLabel('identityVerificationMethod', buyer.identityVerification.method)}${doc}`;
}

function CreditPanel({
  allocations,
  buyer,
  credit,
  onCredit,
  self,
}: {
  readonly allocations: readonly AdminCreditAllocation[];
  readonly buyer: AdminBuyer;
  readonly credit: CreditActionTarget | null;
  readonly onCredit: (target: CreditActionTarget | null) => void;
  readonly self: AdminSelf;
}) {
  const { totals } = buyer.card;
  const rate = creditUsageRate(totals);
  return (
    <div className="admin-sheet-stack">
      <CreditCard
        amount={formatToman(totals.availableCreditIrr, { label: false })}
        amountLabel="اعتبار آزاد"
        badge={
          <StatusChip
            enumName="cardAvailabilityStatus"
            value={buyer.card.card.availabilityStatus}
          />
        }
        details={[
          {
            key: 'granted',
            label: 'اعتبار کل',
            value: <Money rials={totals.currentGrantedCreditIrr} />,
          },
          {
            key: 'outstanding',
            label: 'اصل بدهی',
            value: <Money rials={totals.outstandingPrincipalIrr} />,
          },
          {
            key: 'reserved',
            label: 'رزرو',
            value: <Money rials={totals.reservedPrincipalIrr} />,
          },
        ]}
        title="کارت اعتباری SAAY"
        unit="تومان"
      />
      {rate === null ? null : (
        <CapacityMeter label="مصرف اعتبار" rate={rate} valueText={creditUsageText(totals)} />
      )}
      <Table
        caption="تخصیص‌های اعتبار"
        columns={[
          { header: 'عنوان', key: 'title', pin: 'start', render: (row) => row.titleFa },
          {
            header: 'نوع',
            key: 'type',
            render: (row) => statusLabel('creditAllocationType', row.type),
          },
          {
            header: 'سقف',
            key: 'limit',
            numeric: true,
            render: (row) => (
              <span className="admin-inline">
                <Money rials={row.approvedLimitIrr} />
                {creditActionFor(self, buyer, row) === null ? null : (
                  <IconButton
                    icon="edit"
                    label={row.type === 'BASE' ? 'اصلاح اعتبار پایه' : 'اصلاح سقف'}
                    onClick={() => {
                      const action = creditActionFor(self, buyer, row);
                      if (action !== null) onCredit(action);
                    }}
                    variant="tonal"
                  />
                )}
              </span>
            ),
          },
          {
            header: 'قابل استفاده',
            key: 'available',
            numeric: true,
            render: (row) => <Money rials={row.availableCreditIrr} />,
          },
          {
            header: 'اعتبار تا',
            key: 'until',
            render: (row) =>
              row.validUntil === null
                ? 'بدون انقضا'
                : formatTimestamp(row.validUntil, { withTime: false }),
          },
          {
            header: 'وضعیت',
            key: 'status',
            pin: 'end',
            render: (row) => <StatusChip enumName="creditAllocationStatus" value={row.status} />,
          },
        ]}
        rowKey={(row) => row.id}
        rows={allocations}
      />
      {credit === null ? null : (
        <CreditActionDialog
          buyer={buyer}
          onOpenChange={(next) => {
            if (!next) onCredit(null);
          }}
          target={credit}
        />
      )}
    </div>
  );
}

function BuyerView({ buyer, self }: { readonly buyer: AdminBuyer; readonly self: AdminSelf }) {
  const location = useLocation();
  const quick = (location.state as Record<string, unknown> | null)?.[BUYER_QUICK_ACTION_KEY] as
    BuyerListQuickAction | undefined;
  const { card } = buyer;
  const allocations = (card.creditAllocations ?? []) as readonly AdminCreditAllocation[];
  const [credit, setCredit] = useState<CreditActionTarget | null>(null);
  const [tab, setTab] = useState<BuyerTab>(() => tabFromSearch(location.search, self));
  const [lifecycleOpen, setLifecycleOpen] = useState<'identity' | 'suspend' | null>(null);
  const headline = buyerHeadlineLabel(buyer);
  const useEnumChip = buyer.status !== 'PENDING_VERIFICATION' && buyer.status !== 'SUSPENDED';
  const { search } = useLocation();

  useEffect(() => {
    if (quick === undefined) {
      return;
    }
    if (quick === 'physical-card') {
      setTab('card');
      requestAnimationFrame(() => {
        document.getElementById('buyer-physical-card')?.scrollIntoView({ behavior: 'smooth' });
      });
      return;
    }
    if (quick === 'login-lock') {
      setTab('summary');
      return;
    }
    if (quick === 'identity' || quick === 'suspend') {
      setLifecycleOpen(quick);
    }
  }, [quick]);

  const panels: Record<BuyerTab, ReactNode> = useMemo(
    () => ({
      card: (
        <div className="admin-sheet-stack">
          <PhysicalCardSection buyer={buyer} self={self} />
        </div>
      ),
      credit: (
        <CreditPanel
          allocations={allocations}
          buyer={buyer}
          credit={credit}
          onCredit={setCredit}
          self={self}
        />
      ),
      history: (
        <div className="admin-sheet-stack">
          <FactList
            items={[
              {
                key: 'created',
                label: 'تاریخ ثبت',
                value: formatTimestamp(buyer.createdAt),
              },
              {
                key: 'method',
                label: 'روش ثبت',
                value: statusLabel('buyerProvisioningMethod', buyer.provisioningMethod),
              },
              ...(buyer.statusReason === null
                ? []
                : [
                    {
                      key: 'status-reason',
                      label: 'آخرین دلیل وضعیت',
                      value: buyer.statusReason,
                    },
                  ]),
            ]}
          />
          <AuditTrailLink resourceId={buyer.buyerId} resourceType="BUYER" self={self} />
        </div>
      ),
      organizations:
        buyer.organizationMemberships.length === 0 ? (
          <p className="admin-muted">عضویت سازمانی ندارد.</p>
        ) : (
          <Table
            caption="عضویت‌های سازمانی"
            columns={[
              {
                header: 'سازمان',
                key: 'organization',
                pin: 'start',
                render: (row) => (
                  <Link to={`/organizations/${row.organization.id}`}>
                    {row.organization.displayNameFa}
                  </Link>
                ),
              },
              { header: 'نقش', key: 'role', render: (row) => row.role.nameFa },
              {
                header: 'وضعیت',
                key: 'status',
                pin: 'end',
                render: (row) => <StatusChip enumName="activeStatus" value={row.status} />,
              },
            ]}
            rowKey={(row) => row.membershipId}
            rows={buyer.organizationMemberships}
          />
        ),
      restrictions: (
        <div className="admin-sheet-stack">
          <RestrictionsSection buyer={buyer} self={self} />
        </div>
      ),
      summary: (
        <div className="admin-sheet-stack">
          <FactList
            items={[
              {
                key: 'national',
                label: 'کد ملی',
                value: <Masked kind="nationalCode" value={buyer.nationalCodeMasked} />,
              },
              {
                key: 'mobile',
                label: 'شماره موبایل',
                value:
                  buyer.mobileE164 === undefined ? (
                    <Masked kind="mobile" value={buyer.mobileMasked} />
                  ) : (
                    <Ltr>{formatLocalMobileDisplay(buyer.mobileE164)}</Ltr>
                  ),
              },
              {
                key: 'birth',
                label: 'تاریخ تولد',
                value: formatLocalDate(buyer.birthDate, 'long'),
              },
              {
                key: 'geo',
                label: 'استان / شهر',
                value: `${buyer.province.nameFa} / ${buyer.city.nameFa}`,
              },
              { key: 'verification', label: 'تأیید هویت', value: verificationText(buyer) },
              {
                key: 'guarantors',
                label: 'ضامن‌های خریدار',
                value: `${toPersianDigits(buyer.guaranteeSummary.guarantorCount)} نفر`,
              },
              {
                key: 'guaranteed',
                label: 'ضامن دیگران',
                value: `${toPersianDigits(buyer.guaranteeSummary.guaranteedBuyerCount)} نفر`,
              },
              {
                key: 'agreement',
                label: 'توافق‌نامه ضمانت',
                value: buyer.guaranteeSummary.agreementAccepted ? 'پذیرفته' : 'پذیرفته نشده',
              },
            ]}
          />
          <LoginLockAction
            loginLock={buyer.loginLock}
            personLabel={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
            self={self}
            userId={buyer.buyerId}
          />
        </div>
      ),
      wallet: <BuyerWalletPanel buyerId={buyer.buyerId} self={self} />,
    }),
    [allocations, buyer, credit, self],
  );

  return (
    <>
      <DetailHeader
        actions={
          <BuyerLifecycleActions
            buyer={buyer}
            headlineOnly
            initialOpen={lifecycleOpen}
            onInitialOpenHandled={() => setLifecycleOpen(null)}
            self={self}
            withOverflowMenu
          />
        }
        actionsLabel="کارهای خریدار"
        back={{
          label: 'بازگشت به فهرست',
          state: OPENED_FROM_LIST,
          to: { pathname: '/buyers', search },
        }}
        status={
          <>
            {useEnumChip ? (
              <StatusChip enumName="buyerStatus" value={buyer.status} />
            ) : (
              <StatusChip title={headline} />
            )}
            {card.blockReasons.map((reason) => (
              <StatusChip key={reason} title={statusLabel('blockReason', reason)} />
            ))}
            <PhysicalCardHeader buyer={buyer} self={self} />
          </>
        }
        title={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
        titleId="buyer-detail-title"
      />
      <BuyerPendingApprovals buyer={buyer} />
      {card.blockReasons.length > 0 ? (
        <Banner tone="danger">
          {card.blockReasons.map((reason) => statusLabel('blockReason', reason)).join('، ')}
        </Banner>
      ) : null}
      <h2 className="saay-visually-hidden">بخش‌های خریدار</h2>
      <Tabs
        label="بخش‌های خریدار"
        onChange={setTab}
        panel={panels[tab]}
        tabs={[
          { key: 'summary', label: 'خلاصه' },
          { key: 'credit', label: 'اعتبار' },
          { key: 'card', label: 'کارت' },
          { key: 'restrictions', label: 'محدودیت‌ها' },
          { key: 'organizations', label: 'سازمان‌ها' },
          { key: 'history', label: 'تاریخچه' },
          ...(canReadWallet(self) ? [{ key: 'wallet' as const, label: 'کیف پول' }] : []),
        ]}
        value={tab}
      />
    </>
  );
}

/**
 * `/buyers/:buyerId`: full-page buyer profile (ADM-39) with sticky header and tabs.
 */
export function BuyerDetailSheet() {
  const { self } = useOutletContext<BuyersOutletContext>();
  const { buyerId = '' } = useParams();
  const buyer = useBuyer(buyerId);
  const name =
    buyer.data === undefined ? 'خریدار' : `${buyer.data.firstNameFa} ${buyer.data.lastNameFa}`;
  usePageTitle(name, true, 'پروفایل خریدار');
  return (
    <section
      aria-labelledby="buyer-detail-title"
      className="admin-buyer-detail flex min-w-0 flex-col gap-[var(--saay-layout-section-gap,1.5rem)]"
    >
      <QueryState
        error={buyer.error}
        onRetry={() => void buyer.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری خریدار…" lines={8} />}
        status={buyer.status}
      >
        {buyer.data === undefined ? null : <BuyerView buyer={buyer.data} self={self} />}
      </QueryState>
    </section>
  );
}
